import type { LocaleMessage } from 'vue-i18n'
import type {
  TEditionId,
  TExternalEdition,
  TExternalLicense,
  TExternalProduct,
  TSupportCertificate,
  TSupportCertificateInfo,
  IExternalLicenseResponse,
  TSupportCertificateStatus,
  TSupportCertificateBaseInfo,
  TSupportCertificateInfoResponse,
} from '@/api/services/lk/types'

import {
  getFormattedDate,
  getProductVersion,
  getExpirationDays,
  getFormattedProductEdition,
} from '@/helpers/formatted-functions'
import { App } from '@/main'
import { DATE } from '@/config/constants'

type TExternalProducts = TExternalProduct<TExternalEdition[]>[]

// Названия редакции в словаре
type TEditionNames =
  | 'mailbox-5'
  | 'mailbox-25'
  | 'mailbox-50'
  | 'mailbox-100'
  | 'mailbox-500'
  | 'mailbox-1000'
  | 'workstation'
  | 'virtual-host'
  | 'server'
  | 'universal-license'
  | 'postgresql'

// Название иконок для редакций
type TEditionIconNames =
  | 'servers'
  | 'installer'
  | 'workstation'
  | 'mailboxes'
  | 'postgresql'
  | 'resource-name-4'

const EDITION_ICONS: Record<TEditionId, TEditionIconNames> = {
  '170': 'workstation',
  '172': 'servers',
  '176': 'installer',
  '178': 'mailboxes',
  '179': 'mailboxes',
  '1710': 'mailboxes',
  '1711': 'mailboxes',
  '1712': 'mailboxes',
  '1713': 'mailboxes',
  '1714': 'workstation',
  '1716': 'servers',
  '1718': 'installer',
  '1720': 'resource-name-4',
  '1722': 'postgresql',
  '1724': 'mailboxes',
  '1725': 'mailboxes',
  '1726': 'mailboxes',
  '1727': 'mailboxes',
  '1728': 'mailboxes',
  '1729': 'mailboxes',

  '161': 'servers',
  '160': 'workstation',
  '163': 'installer',
  '1618': 'mailboxes',
  '1619': 'mailboxes',
  '1620': 'mailboxes',
  '164': 'workstation',
  '165': 'servers',
  '166': 'installer',
  '167': 'resource-name-4',
  '1616': 'postgresql',
  '1621': 'mailboxes',
  '1622': 'mailboxes',
  '1623': 'mailboxes',

  '151': 'servers',
  '150': 'workstation',
  '153': 'installer',
  '154': 'workstation',
  '155': 'servers',
  '156': 'installer',
  '157': 'resource-name-4',
}

const EDITION_STAFF_NAMES: Record<TEditionId, TEditionNames> = {
  '170': 'workstation',
  '172': 'server',
  '176': 'virtual-host',
  '178': 'mailbox-5',
  '179': 'mailbox-25',
  '1710': 'mailbox-100',
  '1711': 'mailbox-50',
  '1712': 'mailbox-500',
  '1713': 'mailbox-1000',
  '1714': 'workstation',
  '1716': 'server',
  '1718': 'virtual-host',
  '1720': 'universal-license',
  '1722': 'postgresql',
  '1724': 'mailbox-5',
  '1725': 'mailbox-25',
  '1726': 'mailbox-100',
  '1727': 'mailbox-50',
  '1728': 'mailbox-500',
  '1729': 'mailbox-1000',

  '161': 'server',
  '160': 'workstation',
  '163': 'virtual-host',
  '1618': 'mailbox-5',
  '1619': 'mailbox-25',
  '1620': 'mailbox-1000',
  '164': 'workstation',
  '165': 'server',
  '166': 'virtual-host',
  '167': 'universal-license',
  '1616': 'postgresql',
  '1621': 'mailbox-5',
  '1622': 'mailbox-25',
  '1623': 'mailbox-100',

  '151': 'server',
  '150': 'workstation',
  '153': 'virtual-host',
  '154': 'workstation',
  '155': 'server',
  '156': 'virtual-host',
  '157': 'universal-license',
}

/**
 * getFormattedEditionStaffName
 * ? Возвращает название редакции по идентификатору
 *
 * @param {TEditionId} editionId Идентификатор редакции
 * @returns {LocaleMessage} Название редакции
 */
export const getFormattedEditionStaffName = (
  editionId: TEditionId
): LocaleMessage => {
  return App.$i18n.t(`edition.staff.name.${EDITION_STAFF_NAMES[editionId]}`)
}

/**
 * getEditionIconName
 * ? Возвращает название для иконки редакции по идентификатору
 *
 * @param {TEditionId} editionId Идентификатор редакции
 * @returns {string} Название иконки
 */
export const getEditionIconName = (editionId: TEditionId): string => {
  return EDITION_ICONS[editionId]
}

const isEditionId = (value: string): value is TEditionId => {
  return value in EDITION_ICONS && value in EDITION_STAFF_NAMES
}

/**
 * getFormattedProduct
 * ? Возвращает продукт лицензии
 *
 * @param {IExternalLicenseResponse} license лицензия
 * @param {TExternalEdition[]} editions редакции в продукте
 * @returns {TExternalProduct} продукт
 */
export const getFormattedProduct = (
  license: IExternalLicenseResponse,
  editions: TExternalEdition[] = []
): TExternalProduct => {
  return {
    id: getProductVersion(license.product_name) + license.edition_type,
    product_id: license.product_id,
    product_name: getFormattedProductEdition(
      license.product_name,
      license.edition_type
    ),
    edition_type: license.edition_type,
    product_version: getProductVersion(license.product_last_version_name ?? ''),
    editions: new Map(editions.map((item) => [item.id, item])),
  }
}

/**
 * getFormattedEdition
 * ? Возвращает редакцию лицензии
 *
 * @param {IExternalLicenseResponse} license лицензия
 * @param {TExternalLicense[]} licenses лицензии в редакции
 * @returns {TExternalEdition} редакция
 */
export const getFormattedEdition = (
  license: IExternalLicenseResponse,
  licenses: TExternalLicense[] = []
): TExternalEdition => {
  const id = (getProductVersion(license.product_name) +
    license.edition_id) as TEditionId

  return {
    id,
    ttl_id: license.ttl_id,
    icon_name: isEditionId(id) ? getEditionIconName(id) : 'resource-name-4',
    edition_id: license.edition_id,
    edition_type: license.edition_type,
    edition_name: isEditionId(id)
      ? getFormattedEditionStaffName(id)
      : license.edition_name.replace(/(advanced|standard)/gi, '').trim(),
    licenses,
  }
}

/**
 * getFormattedLicense
 * ? Возвращает отформатированную лицензию
 *
 * @param {IExternalLicenseResponse} license лицензия
 * @returns {TExternalLicense} лицензия
 */
export const getFormattedLicense = (
  license: IExternalLicenseResponse
): TExternalLicense => {
  return {
    id: license.id,
    comment: license?.comment ?? '',
    personal: license.personal,
    order_number: license.order_number,
    linked_account: license.linked_account,
    upgrade_license_id: license.upgrade_license_id,
    created_date: license.created_date,
    formatted_created_date: getFormattedDate(license.created_date),
    subscription_period: App.$i18n.t('component.tag.name.perpetual-license'),
    key_subscription_period: App.$i18n.t('component.table.cell.perpetual-key'),
  }
}

/**
 * isValidForTransfer
 * ? Подтверждает возможность передачи лицензии
 *
 * @param {TExternalLicense} license лицензия
 * @returns {TExternalLicense} возможность передать лицензию
 */
export const isValidForTransfer = (license: TExternalLicense): boolean => {
  return !license.personal
}

/**
 * getGroupedMapLicensesByProduct
 * ? Возвращает мапу лицензий сгруппированных по продуктам и редакциям
 *
 * @param {IExternalLicenseResponse[]} licenses лицензии
 * @returns {Map<string, TExternalProduct>} сгруппированный список
 */
export const getGroupedMapLicensesByProduct = (
  licenses: IExternalLicenseResponse[]
): Map<string, TExternalProduct> => {
  return licenses.reduce((acc, item) => {
    const license = getFormattedLicense(item)
    const edition = getFormattedEdition(item, [license])
    const product = getFormattedProduct(item, [edition])

    // Группировка по версии продукта
    if (!acc.has(product.id)) {
      acc.set(product.id, product)
      return acc
    }

    // Группировка по редакции
    if (!acc.get(product.id).editions.has(edition.id)) {
      acc.get(product.id).editions.set(edition.id, edition)
      return acc
    }

    acc.get(product.id).editions.get(edition.id).licenses.push(license)

    return acc
  }, new Map())
}

/**
 * getGroupedListLicensesByProduct
 * ? Возвращает список лицензий сгруппированных по продуктам с подсчетом количества лицензий и флагом о наличии обновленных лицензий
 *
 * @param {Map<string, TExternalProduct>} productGroups лицензии
 * @returns {TExternalProducts} список продуктов с лицензиями
 */
export const getGroupedListLicensesByProduct = (
  productGroups: Map<string, TExternalProduct>
): TExternalProducts => {
  const getEditions = (editions: Map<string, TExternalEdition>) => {
    return Array.from(editions.values())
      .map((item: TExternalEdition) => {
        // Лицензии без обновления
        const licenses = item.licenses.filter(
          (item) => item.upgrade_license_id === undefined
        )

        return {
          ...item,
          licenses_count: licenses.length,
          has_upgraded_licenses: licenses.length !== item.licenses.length,
          licenses,
        }
      })
      .filter((edition: TExternalEdition) => edition.licenses_count ?? 0 > 0)
  }

  const licenses: TExternalProducts = Array.from(productGroups.values()).reduce(
    (acc: TExternalProducts, product: TExternalProduct) => {
      const editions: TExternalEdition[] = getEditions(product.editions)

      // Пропускаем редакции с обновленными лицензиями
      if (editions.length === 0) {
        return acc
      }

      acc.push({
        ...product,
        editions,
      })

      return acc
    },
    []
  )

  // TODO: Временное решение пока в АПИ не сделают параметр для сортировки.
  licenses.forEach((product: TExternalProducts[number]) => {
    product.editions.sort(
      (a: TExternalEdition, b: TExternalEdition) => a.edition_id - b.edition_id
    )
  })

  return licenses
}

/**
 * ? Обрабатывает статус добавляя дополнительный статус "expires" у сертификатов которые заканчиваются через 14 дней
 *
 * @param {TSupportCertificateInfoResponse} certificate информация о сертификате
 * @returns {TSupportCertificateStatus} статус сертификата
 */
const getSupportCertificateStatus = (
  certificate: TSupportCertificateInfoResponse
): TSupportCertificateStatus => {
  const expirationDays = getExpirationDays(certificate.date_end)

  if (
    !certificate.date_end ||
    certificate.date_end === DATE.INVALID_DATE ||
    certificate.status === 'inactive' ||
    expirationDays <= 0
  ) {
    return certificate.status
  }

  return expirationDays <= 14 ? 'expires' : certificate.status
}

/**
 * ? Возвращает преобразованный сертификат для отображения в UI
 *
 * @param {TSupportCertificateInfoResponse} certificate информация о сертификате
 * @param {TSupportCertificateBaseInfo} certificateBaseInfo информация о лицензии на сертификат
 * @returns {TSupportCertificateInfo} сертификат
 */
export const getFormatSupportCertificate = (
  certificate: TSupportCertificateInfoResponse,
  certificateBaseInfo: TSupportCertificateBaseInfo
): TSupportCertificateInfo => {
  const status = getSupportCertificateStatus(certificate)
  const isExpires = status === 'expires'

  const tagVariants: Record<
    TSupportCertificateStatus,
    TSupportCertificateInfo['tagVariant']
  > = {
    active: 'outline-success',
    expires: 'outline-warning',
    inactive: 'outline-inactive',
  }

  const formatStatusKeys: Record<TSupportCertificateStatus, string> = {
    active: 'page.products.support-certificates.status.active',
    expires: 'page.products.support-certificates.status.expires',
    inactive: 'page.products.support-certificates.status.expired',
  }

  return {
    ...certificate,
    ...certificateBaseInfo,
    isExpires,
    tagVariant: tagVariants[status],
    formatStatus: App.$i18n.t(formatStatusKeys[status]),
    formatEndDate: getFormattedDate(certificate.date_end),
    formatStartDate: getFormattedDate(certificate.date_start),
  }
}

/**
 * ? Возвращает сгруппированный список сертификатов по продукту и редакции
 *
 * @param {TSupportCertificate[]} certificates список сертификатов
 * @returns {TSupportCertificateInfo} сгруппированный список сертификатов
 */
export const getGroupedListSupportCertificates = (
  certificates: TSupportCertificate[]
): Map<number, Map<number, TSupportCertificate[]>> => {
  return certificates.reduce((acc, item) => {
    if (!acc.has(item.product_id)) {
      acc.set(item.product_id, new Map())
    }

    if (!acc.get(item.product_id)?.has(item.edition_id)) {
      acc.get(item.product_id)?.set(item.edition_id, [])
    }

    acc.get(item.product_id)?.get(item.edition_id)?.push(item)

    return acc
  }, new Map())
}
